import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";
import { apiClient } from "./client";

/**
 * Phase 1 MFA API (backend.md §24.6).
 *
 * `POST /me/mfa/enroll` + `/verify` need the Cognito **AccessToken**, not
 * the ID token our axios interceptor attaches to every other request.
 * Amplify exposes both on the session; we read the access token on demand
 * and pass it in the body. Backend's cognito-idp admin calls don't need
 * it; the endpoint just forwards to AssociateSoftwareToken / Verify-
 * SoftwareToken which are account-owner operations.
 */

export type MfaMethod = "none" | "totp";

export interface MfaStatus {
  method: MfaMethod;
  mfa_required: boolean;
  required_by_tenant: boolean;
}

export interface MfaEnrollResponse {
  secret_code: string;
  otpauth_url: string;
}

async function requireAccessToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();
  if (!token) {
    throw new Error("No Cognito access token — sign in again.");
  }
  return token;
}

export function useMfaStatus() {
  return useQuery<MfaStatus>({
    queryKey: ["me", "mfa"],
    queryFn: async () => (await apiClient.get<MfaStatus>("/me/mfa")).data,
    staleTime: 30_000,
  });
}

export function useEnrollMfa() {
  return useMutation<MfaEnrollResponse, Error, void>({
    mutationFn: async () => {
      const access_token = await requireAccessToken();
      return (
        await apiClient.post<MfaEnrollResponse>("/me/mfa/enroll", { access_token })
      ).data;
    },
  });
}

export function useVerifyMfa() {
  const qc = useQueryClient();
  return useMutation<MfaStatus, Error, { code: string }>({
    mutationFn: async ({ code }) => {
      const access_token = await requireAccessToken();
      return (
        await apiClient.post<MfaStatus>("/me/mfa/verify", { access_token, code })
      ).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "mfa"] }),
  });
}

export function useDisableMfa() {
  const qc = useQueryClient();
  return useMutation<MfaStatus, Error, void>({
    mutationFn: async () =>
      (await apiClient.post<MfaStatus>("/me/mfa/disable")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "mfa"] }),
  });
}
